/* eslint-disable react-native/no-inline-styles */
import { format } from 'date-fns';
import { Notification, NotificationStatusId, NotificationTypeId } from '@packages/models/api';
import { TouchableOpacity, View, StyleSheet } from 'react-native';
import { Typography } from './typography';
import { UnreadStatusIndicator } from './unread-status-indicator';
import colors from '@packages/core/styles/colors';
import React, { FC, useMemo } from 'react';

interface NotificationProps {
    item: Notification;
    fluid?: boolean;
    onPress(notification: Notification): void;
}

export const NotificationListItem: FC<NotificationProps> = ({ item, fluid, onPress }) => {
    function handleNotificationPress() {
        onPress(item);
    }

    const isNew =
        item.notificationStatusId === NotificationStatusId.New ||
        item.notificationStatusId === NotificationStatusId.Unread;

    const formattedTimestamp = useMemo(() => {
        const timestampDate = new Date(item.createdTimestamp);
        const formatString = item.notificationGroupingId === 'TODAY' ? 'h:mm a' : 'EEE dd, yyyy - HH:mm:ss';
        return format(timestampDate, formatString);
    }, [item.createdTimestamp, item.notificationGroupingId]);

    const displayMessage = useMemo(() => {
        if (item.notificationTypeId === NotificationTypeId.OtpwAssignment) {
            return 'Action required: Key reprogramming authorization assigned';
        }
        return item.message;
    }, [item.message, item.notificationTypeId]);

    const vehicleInfo = useMemo(() => {
        if (item.notificationTypeId === NotificationTypeId.OtpwAssignment) {
            return item.data?.vin || '';
        }
        return `${item.data?.vehicleModelYear || ''} ${item.data?.carlineName || ''}`.trim();
    }, [item.data, item.notificationTypeId]);

    return (
        <TouchableOpacity
            style={[
                styles.notificationListItem,
                {
                    paddingHorizontal: fluid ? 0 : 24,
                    backgroundColor: isNew ? colors.white : colors.grayOne,
                },
            ]}
            onPress={handleNotificationPress}
        >
            <View style={styles.notificationMessageOuter}>
                <Typography
                    variant={isNew ? 'label' : 'labelRegular'}
                    style={styles.notificationMessage}
                    color="blueOne"
                >
                    {displayMessage}
                </Typography>
                <View style={styles.notificationTimestampOuter}>
                    <Typography variant="caption" color="graySix">
                        {formattedTimestamp}
                    </Typography>
                    {isNew && <UnreadStatusIndicator />}
                </View>
            </View>
            {!!vehicleInfo && (
                <Typography variant="caption" style={styles.notificationVehicleText}>
                    {vehicleInfo}
                </Typography>
            )}

            {!!item.retailerName && (
                <Typography variant="caption" color="graySix">
                    {item.retailerName}
                    {!!item.retailerCode && ` (${item.retailerCode})`}
                </Typography>
            )}
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    notificationListItem: {
        paddingVertical: 16,
    },
    notificationTextBlue: {
        fontSize: 15,
        fontWeight: 'bold',
        color: colors.blueOne,
    },
    statusPill: {
        fontSize: 12,
        textTransform: 'uppercase',
        backgroundColor: colors.greenFour,
    },
    notificationMessageOuter: {
        marginBottom: 8,
        flexDirection: 'row',
    },
    notificationMessage: {
        flex: 1,
    },
    notificationTimestampOuter: {
        flexShrink: 0,
        paddingLeft: 16,
        flexDirection: 'row',
        justifyContent: 'center',
    },
    notificationVehicleText: {
        marginBottom: 8,
    },
});
